import * as Popover from '@radix-ui/react-popover'
import { useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useRouterState } from '@tanstack/react-router'
import { ChevronsUpDown, LogOut } from 'lucide-react'
import { useState } from 'react'
import { authKeys, logout, useAuth } from '@/lib/auth'
import { navForRole, type NavItem } from '@/lib/nav'
import { cn } from '@/lib/utils'
import { BrandLogo } from './BrandLogo'

function isActive(item: NavItem, pathname: string): boolean {
  if (item.matchPrefix) return pathname === item.to || pathname.startsWith(item.matchPrefix)
  return pathname === item.to
}

export function SidebarNav({ onNavigate }: { onNavigate?: () => void }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const auth = useAuth()
  const items = navForRole(auth.data?.user.role, auth.data?.user.personScoped)

  return (
    <nav className="flex flex-1 flex-col gap-0.5 px-3">
      {items.map((item) => {
        const active = isActive(item, pathname)
        const Icon = item.icon
        return (
          <Link
            key={item.to}
            to={item.to}
            onClick={onNavigate}
            className={cn(
              'group flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-150',
              active ? 'bg-accent-600/10 text-accent-700' : 'text-ink-soft hover:bg-fill-2 hover:text-ink',
            )}
          >
            <Icon
              className={cn('h-[18px] w-[18px]', active ? 'text-accent-600' : 'text-ink-faint group-hover:text-ink-soft')}
              strokeWidth={active ? 2.1 : 1.85}
            />
            {item.label}
          </Link>
        )
      })}
    </nav>
  )
}

export function Sidebar() {
  return (
    <aside className="hidden w-60 shrink-0 flex-col lg:flex">
      <Brand />
      <SidebarNav />
      <SidebarFooter />
    </aside>
  )
}

export function Brand() {
  return (
    <div className="flex h-14 items-center px-5">
      <BrandLogo />
    </div>
  )
}

function Avatar({ initials }: { initials: string }) {
  return (
    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent-600/12 text-xs font-semibold text-accent-700">
      {initials}
    </div>
  )
}

export function SidebarFooter({ onNavigate }: { onNavigate?: () => void }) {
  const auth = useAuth()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const user = auth.data?.user
  const initials = user ? user.displayName.split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase() : '··'

  async function handleLogout() {
    setOpen(false)
    onNavigate?.()
    await logout()
    await queryClient.invalidateQueries({ queryKey: authKeys.me })
    queryClient.clear()
    await navigate({ to: '/login' })
  }

  return (
    <div className="px-3 py-4">
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <button
            type="button"
            className="flex w-full items-center gap-3 rounded-xl bg-surface px-2.5 py-2 text-left shadow-card transition-colors hover:bg-fill-1 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-accent-500/40 data-[state=open]:bg-fill-1"
          >
            <Avatar initials={initials} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-ink">{user?.displayName ?? 'Laden…'}</p>
              <p className="truncate text-xs text-ink-faint">{user?.email ?? ''}</p>
            </div>
            <ChevronsUpDown className="h-4 w-4 shrink-0 text-ink-faint" strokeWidth={1.85} />
          </button>
        </Popover.Trigger>
        <Popover.Content
          side="top"
          align="start"
          sideOffset={8}
          className="z-40 w-(--radix-popover-trigger-width) min-w-56 rounded-card border border-hairline bg-surface p-1.5 shadow-pop animate-fade-in"
        >
          <div className="flex items-center gap-3 px-2.5 py-2">
            <Avatar initials={initials} />
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-ink">{user?.displayName ?? ''}</p>
              <p className="truncate text-xs text-ink-faint">{user?.email ?? ''}</p>
            </div>
          </div>
          <div className="my-1 h-px bg-hairline" />
          <button
            type="button"
            onClick={handleLogout}
            className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm text-ink transition-colors hover:bg-fill-2"
          >
            <LogOut className="h-4 w-4 text-ink-faint" strokeWidth={1.85} />
            Uitloggen
          </button>
        </Popover.Content>
      </Popover.Root>
    </div>
  )
}
